/**
 * Type for parameter values that can be nested objects, arrays, or primitives
 */
export type ParamValue = string | number | boolean | null | undefined | ParamValue[] | { [key: string]: ParamValue };

/**
 * Type for parameter objects that can be flattened into URL search params
 */
export type ParamsObject = Record<string, ParamValue>;

/**
 * Options for controlling the flattening behavior
 */
export interface FlattenObjectOptions {
  /**
   * Keys to skip during flattening
   * @default []
   */
  skipKeys?: string[];
  /**
   * Custom function to format the parameter key
   * @param key - The current key
   * @param prefix - The current prefix (if any)
   * @returns The formatted parameter key
   * @default Uses bracket notation for nested keys (e.g., "parent[child]")
   */
  formatKey?: (key: string, prefix: string) => string;
}

/**
 * Flattens a nested object into URL search parameters format.
 *
 * @description
 * This utility function takes a nested object and flattens it into a format suitable
 * for URL query parameters. It handles primitives, arrays, and nested objects using
 * bracket notation for nested keys.
 *
 * @example
 * ```typescript
 * const params = { page: { number: 1, size: 10 }, filter: "active" };
 * const searchParams = new URLSearchParams();
 * flattenObject(params, searchParams);
 * // Result: page[number]=1&page[size]=10&filter=active
 * ```
 *
 * @example
 * ```typescript
 * // With arrays
 * const params = { ids: [1, 2, 3], status: "active" };
 * const searchParams = new URLSearchParams();
 * flattenObject(params, searchParams);
 * // Result: ids[]=1&ids[]=2&ids[]=3&status=active
 * ```
 *
 * @example
 * ```typescript
 * // With options to skip certain keys
 * const params = { page: 1, query: { internal: true }, data: "test" };
 * const searchParams = new URLSearchParams();
 * flattenObject(params, searchParams, { skipKeys: ['query'] });
 * // Result: page=1&data=test (query is skipped)
 * ```
 *
 * @param obj - The object to flatten into URL search parameters
 * @param searchParams - The URLSearchParams instance to append parameters to
 * @param options - Optional configuration for the flattening behavior
 * @param prefix - Internal parameter used for recursion to track the current key prefix
 * @returns void - Modifies the provided URLSearchParams instance directly
 */
export function flattenObject(
  obj: ParamsObject,
  searchParams: URLSearchParams,
  options: FlattenObjectOptions = {},
  prefix = "",
): void {
  const { skipKeys = [], formatKey } = options;

  for (const [key, value] of Object.entries(obj)) {
    // Skip specified keys
    if (skipKeys.includes(key)) continue;

    if (value !== undefined && value !== null) {
      // Use custom formatter if provided, otherwise use default bracket notation
      const paramKey = formatKey ? formatKey(key, prefix) : prefix ? `${prefix}[${key}]` : key;

      // Handle arrays by appending each value with brackets notation
      if (Array.isArray(value)) {
        // For arrays, append [] to the key name to indicate it's an array parameter
        const arrayKey = `${paramKey}[]`;
        for (const item of value) {
          if (item !== undefined && item !== null) {
            searchParams.append(arrayKey, String(item));
          }
        }
      }
      // Handle nested objects recursively
      else if (typeof value === "object") {
        flattenObject(value as ParamsObject, searchParams, options, paramKey);
      }
      // Handle primitive values
      else {
        searchParams.append(paramKey, String(value));
      }
    }
  }
}
